/**
 * RETURNS / LANGUAGE / body delimiting for the parked-routine parser
 * (VDE-2 Slice 4). Split out of `routine.parse.ts` to keep each module under
 * the 200-line file budget while preserving the byte-faithful contract
 * documented on `routine.parse.ts`.
 *
 * Regex-free (project rule VQE-D10). Every matcher here was fuzz-verified
 * byte-identical to the removed parser's regex — the RETURNS lazy-up-to-stop-
 * set scan, the `\bLANGUAGE\s+IDENT` scan, the `(?:^|\s)(?:AS|IS)\s+` body-
 * start scan, the `$tag$ … $tag$` backreference for dollar-quoted bodies and
 * the `\bBEGIN\b` fallback. ABSOLUTELY no regex into the routine body or the
 * dollar-quote span (the entire point of the slice).
 */

import { isWordChar, isWs, skipSpacesLocal, isIdentStart } from './routine.parse.lex';

const RETURNS_STOP_BARE: readonly string[] = [
  'BEGIN', 'DETERMINISTIC', 'VOLATILE', 'STABLE', 'IMMUTABLE', 'STRICT',
  'SECURITY', 'AUTHID', 'READS', 'MODIFIES', 'CONTAINS', 'WITH',
];

/** True when the removed parser's RETURNS lookahead
 *  `(?=\s+(?:LANGUAGE|AS\s|IS\s|BEGIN\b|…|WITH\b)|\s*$)` succeeds at `c`.
 *  Stop tokens are preceded by `\s+`; `LANGUAGE` is NOT `\b`-bounded (regex
 *  had no `\b`); `AS`/`IS` require a trailing ws char; `NO\s+SQL\b` is a two-
 *  word phrase; the rest are `\b`-bounded; `\s*$` ends the scan at trailing-
 *  ws-then-end. */
function returnsLookaheadStop(source: string, c: number, end: number): boolean {
  let w = c;
  while (w < end && isWs(source[w])) w++;
  if (w > c) {
    if (w + 2 < end && source.slice(w, w + 2).toUpperCase() === 'AS' && isWs(source[w + 2])) return true;
    if (w + 2 < end && source.slice(w, w + 2).toUpperCase() === 'IS' && isWs(source[w + 2])) return true;
    if (source.slice(w, w + 8).toUpperCase() === 'LANGUAGE') return true;
    if (source.slice(w, w + 2).toUpperCase() === 'NO') {
      const n2 = w + 2;
      let n3 = n2;
      while (n3 < end && isWs(source[n3])) n3++;
      if (n3 > n2 && source.slice(n3, n3 + 3).toUpperCase() === 'SQL') {
        const nx = source[n3 + 3];
        if (!(nx !== undefined && n3 + 3 < end && isWordChar(nx))) return true;
      }
    }
    for (const tok of RETURNS_STOP_BARE) {
      if (source.slice(w, w + tok.length).toUpperCase() === tok) {
        const nx = source[w + tok.length];
        if (!(nx !== undefined && w + tok.length < end && isWordChar(nx))) return true;
      }
    }
  }
  let z = c;
  while (z < end && isWs(source[z])) z++;
  return z === end;
}

/** `^(?:RETURNS|RETURN)\s+([\s\S]*?)(?=STOP|\s*$)` over `[from, end)` —
 *  `RETURNS` is tried before `RETURN`; the lazy capture extends to the
 *  earliest position where `returnsLookaheadStop` holds. Returns the raw
 *  captured text (the caller `.trim()`s it), or null when `from` does not
 *  start with `RETURN(S)\s+`. */
export function matchReturnsText(source: string, from: number, end: number): string | null {
  let kw = -1;
  if (source.slice(from, from + 7).toUpperCase() === 'RETURNS') kw = from + 7;
  else if (source.slice(from, from + 6).toUpperCase() === 'RETURN') kw = from + 6;
  if (kw === -1) return null;
  const capStart = skipSpacesLocal(source, kw, end);
  if (capStart === kw) return null; // \s+
  for (let c = capStart; c <= end; c++) {
    if (returnsLookaheadStop(source, c, end)) return source.slice(capStart, c);
  }
  // Unreachable: `returnsLookaheadStop` always returns true at `c === end`
  // (the `\s*$` branch of the removed regex's lookahead). Kept as a defensive
  // fallback mirroring that `|\s*$` alternative.
  return source.slice(capStart, end);
}

/** `\bLANGUAGE\s+([A-Za-z_][A-Za-z0-9_]*)` anywhere in `[from, end)`. The
 *  `\b` is relative to the scan-window start (= the removed parser's
 *  `tail`). Returns the captured language identifier, or null. */
export function matchLanguage(source: string, from: number, end: number): string | null {
  for (let i = from; i + 8 <= end; i++) {
    if (source.slice(i, i + 8).toUpperCase() !== 'LANGUAGE') continue;
    if (i > from && isWordChar(source[i - 1])) continue; // \b
    const p = i + 8;
    const q = skipSpacesLocal(source, p, end);
    if (q === p) continue; // \s+
    if (!isIdentStart(source[q])) continue;
    let r = q + 1;
    while (r < end && isWordChar(source[r])) r++;
    return source.slice(q, r);
  }
  return null;
}

/** `(?:^|\s)(?:AS|IS)\s+([\s\S]*)$` — leftmost word-position `AS`/`IS`
 *  followed by `\s+`; the body is the verbatim remainder to `end`. Returns
 *  the raw remainder (caller `.trim()`s it), or null. */
export function matchAsBody(source: string, from: number, end: number): string | null {
  for (let i = from; i + 2 <= end; i++) {
    if (!(i === from || isWs(source[i - 1]))) continue;
    const two = source.slice(i, i + 2).toUpperCase();
    if (two !== 'AS' && two !== 'IS') continue;
    const after = i + 2;
    const ws = skipSpacesLocal(source, after, end);
    if (ws === after) continue; // \s+
    return source.slice(ws, end);
  }
  return null;
}

/** `^(\$[A-Za-z_]*\$)([\s\S]*?)\1\s*;?\s*$` on an already-trimmed body — the
 *  `\1` backreference means the CLOSING tag must equal the OPENING one. We
 *  capture the opening `$[A-Za-z_]*$` run, then string-match the SAME run at
 *  the earliest later position whose remainder is `\s*;?\s*$`. Returns the
 *  inner content, or null. */
export function matchDollarBody(body: string): string | null {
  if (body[0] !== '$') return null;
  let j = 1;
  while (j < body.length && isIdentStart(body[j])) j++;
  if (body[j] !== '$') return null;
  const tag = body.slice(0, j + 1);
  const innerStart = j + 1;
  for (let k = innerStart; k + tag.length <= body.length; k++) {
    if (body.slice(k, k + tag.length) !== tag) continue;
    let p = k + tag.length;
    while (p < body.length && isWs(body[p])) p++;
    if (body[p] === ';') p++;
    while (p < body.length && isWs(body[p])) p++;
    if (p === body.length) return body.slice(innerStart, k);
  }
  return null;
}

/** `\bBEGIN\b[\s\S]*$` over `[from, end)` (the fallback when no `AS`/`IS`).
 *  Returns the raw `BEGIN…end` slice (caller `.trim()`s it), or null. */
export function matchBeginBody(source: string, from: number, end: number): string | null {
  for (let i = from; i + 5 <= end; i++) {
    if (source.slice(i, i + 5).toUpperCase() !== 'BEGIN') continue;
    if (i > from && isWordChar(source[i - 1])) continue; // \b
    const nx = source[i + 5];
    if (nx !== undefined && i + 5 < end && isWordChar(nx)) continue; // \b
    return source.slice(i, end);
  }
  return null;
}
